import {
  SPEECH_SUPER_CONNECTION_MANAGER_OPTIONS,
  SpeechSuperCoreTypeEnum,
} from '../../constants/speechsuper.constants.ts';

import { SpeechSuperConnectionPool } from './speechsuper-connection-pool.ts';
import { SpeechSuperConnection } from './speechsuper-connection.ts';

/**
 * Utility class to manage WebSocket connections to SpeechSuper.
 * Handles connection pooling and message buffering.
 */
export class SpeechSuperConnectionManagerUtil {
  // Singleton instance
  private static _instance: SpeechSuperConnectionManagerUtil;

  static get instance(): SpeechSuperConnectionManagerUtil {
    if (!SpeechSuperConnectionManagerUtil._instance) {
      SpeechSuperConnectionManagerUtil._instance = new SpeechSuperConnectionManagerUtil();
    }
    return SpeechSuperConnectionManagerUtil._instance;
  }

  // Instance properties
  private readonly pools = new Map<SpeechSuperCoreTypeEnum, SpeechSuperConnectionPool>();
  private intervalId: NodeJS.Timeout | null = null;

  private constructor() {
    this.startCleanupInterval();
  }

  /**
   * Gets a connection for the specified core type.
   * If no idle connection is available, creates a new one.
   * @param coreType - The type of core to get a connection for
   * @returns A valid connection that can be used for assessments
   */
  getConnection(coreType: SpeechSuperCoreTypeEnum): SpeechSuperConnection {
    const pool = this.getOrCreatePool(coreType);
    const existingConnection = pool.getIdleConnection();

    if (existingConnection?.isValid(SPEECH_SUPER_CONNECTION_MANAGER_OPTIONS.MAX_IDLE_TIME)) {
      return existingConnection;
    }

    return pool.createConnection();
  }

  /**
   * Disposes of all connections and cleans up resources.
   * Should be called when the manager is no longer needed.
   */
  dispose(): void {
    this.pools.forEach((pool) => {
      pool.cleanUp();
    });
    this.pools.clear();
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  private getOrCreatePool(coreType: SpeechSuperCoreTypeEnum): SpeechSuperConnectionPool {
    let pool = this.pools.get(coreType);
    if (!pool) {
      pool = new SpeechSuperConnectionPool(coreType);
      this.pools.set(coreType, pool);
    }
    return pool;
  }

  private startCleanupInterval(): void {
    this.intervalId = setInterval(() => {
      for (const pool of this.pools.values()) {
        const expiredConnections = pool.cleanupExpiredConnections(
          SPEECH_SUPER_CONNECTION_MANAGER_OPTIONS.MAX_IDLE_TIME
        );
        expiredConnections.forEach((conn) => {
          void conn.close();
        });
      }
    }, SPEECH_SUPER_CONNECTION_MANAGER_OPTIONS.VERIFY_CONNECTION_INTERVAL);
  }
}

/**
 * Gets the singleton instance of SpeechSuperConnectionManager.
 * @returns The singleton instance of the connection manager
 */
export const getSpeechSuperConnectionManager = (): SpeechSuperConnectionManagerUtil =>
  SpeechSuperConnectionManagerUtil.instance;
